/**
 * Who may do what, asked the way APR's screens ask it.
 *
 * Precedent: `apps/portal/web/src/pages/apr/compat/AuthContext.ts:4-29` and its use at
 * `pages/apr/settings/ScoringModel.tsx:60`, `:133`, `:289`. Cloned pattern, not imported
 * (D-003); the roles are this module's own six, named by API_CONTRACT §1.9 from `0099` and
 * `0090`'s stored descriptions — and `role_name` is "Administrator", per **D-011**.
 *
 * THE DECISIVE SENTENCE, which is `AuthContext.ts:26-28` and API_CONTRACT §1.8 saying the
 * same thing twice: **"The UI check is a convenience, never the boundary."** The server
 * re-checks and answers `403 role_not_permitted` with `required` and `held`. A wrong answer
 * here hides or shows a button; it does not decide what the API will accept. So every screen
 * in this module does BOTH — omits the control (§17.3: hidden, not disabled) AND handles the
 * 403, because the two can disagree when a role is revoked while a screen is open.
 *
 * Platform administrators and Talogent staff satisfy every requirement below (§1.8), which is
 * the rule `ScoringModel.tsx`'s `canManageSettings` already follows.
 */

import { type ErpContext } from './types';

export const ERP_ROLE = {
  administrator: 'erp_administrator',
  leadArchitect: 'erp_lead_architect',
  contributor: 'erp_contributor',
  approver: 'erp_approver',
  viewer: 'erp_viewer',
  systemMaintainer: 'erp_system_maintainer',
} as const;

function holds(context: ErpContext | null, ...roleKeys: string[]): boolean {
  if (!context) return false;
  if (context.user.isAdmin || context.user.isTalogent) return true;
  return context.roles.some((role) => roleKeys.includes(role.roleKey));
}

/**
 * Creating, editing, deleting and baselining an INITIATIVE, and owning business units.
 * §4.4, §4.6, §4.7, §4.8, §5.2-§5.4 — all `erp_administrator`.
 *
 * NOT the Lead Architect: `0099` gives that role the capability model, not the initiative
 * header (§4.6). And §10.4 records that whether the Lead Architect may baseline the
 * INITIATIVE is not settled by `0099` and is not guessed at — so this does not guess either.
 */
export const canAdministerInitiatives = (context: ErpContext | null): boolean =>
  holds(context, ERP_ROLE.administrator);

/**
 * The capability model: its tree, its weights, and baselining it.
 * §6.8, §6.11-§6.17 — `erp_lead_architect` or `erp_administrator`.
 *
 * Explicitly NOT `erp_contributor`, whose own stored description says so: *"Cannot change the
 * capability model or its weights, cannot baseline, cannot configure scoring."*
 */
export const canEditCapabilityModel = (context: ErpContext | null): boolean =>
  holds(context, ERP_ROLE.leadArchitect, ERP_ROLE.administrator);

/**
 * Baselining the MODEL — §6.12, `erp_lead_architect` or `erp_administrator`, and explicitly
 * not `erp_approver`: *"baselining is a professional judgement about the model, approval is a
 * governance decision about the money."*
 */
export const canBaselineCapabilityModel = canEditCapabilityModel;

/**
 * DELETING a capability model — §6.20, `erp_administrator` **only**, which is narrower than every
 * other write on a model.
 *
 * Its own function rather than a reuse of `canAdministerInitiatives`: the two resolve to the same
 * role today and answer different questions, so a later ruling moves one line here instead of a
 * hunt through call sites — the reason `canManageTemplateLibrary` is its own function too.
 *
 * The server's reason for the narrowing, stated so the screen does not look arbitrary: this is the
 * ONLY irreversible operation on a capability model. A weight, a name, a structure and even a
 * baseline can be read off the audit trail and written back; a deleted artefact cannot.
 * **Consequence, stated rather than hidden:** a Lead Architect does not see the bin on their own
 * draft either. They can empty it capability by capability, or ask an administrator.
 */
export const canDeleteCapabilityModel = (context: ErpContext | null): boolean =>
  holds(context, ERP_ROLE.administrator);

/**
 * THE TEMPLATE LIBRARY — creating, editing and deleting a template, its lines and their weights.
 * §6.3-§6.7 — `erp_administrator`, every one of them.
 *
 * Its own function rather than a reuse of `canAdministerInitiatives`: the two happen to resolve to
 * the same role today and they answer different questions, so a later ruling that the Lead
 * Architect owns the library would be one line here instead of a hunt through call sites. The
 * library is global to the install (D-002, there is no tenant to scope it to) and §1.9 gives the
 * Administrator exactly this: *"owns scoring configuration, owns the template library."*
 *
 * READING it is all six roles (§6.1, §6.2), which is why the screen's table renders for everyone
 * and only its controls are gated.
 */
export const canManageTemplateLibrary = (context: ErpContext | null): boolean =>
  holds(context, ERP_ROLE.administrator);

/**
 * ENTERING ASSESSMENT DATA — the grid's cells and IMP-01's two numbers.
 * §7b.6, §7b.7, §7b.8 — `erp_contributor`, `erp_lead_architect` or `erp_administrator`.
 *
 * API_CONTRACT §1.9, of the Contributor: *"enters the data the assessment runs on."* The
 * Viewer, the Approver and the System Maintainer read the grid and do not write it, so the
 * cell inputs are not rendered for them at all (§17.3: hidden, not disabled) — and the server
 * refuses independently, which is the half that actually decides.
 */
export const canEnterAssessments = (context: ErpContext | null): boolean =>
  holds(context, ERP_ROLE.contributor, ERP_ROLE.leadArchitect, ERP_ROLE.administrator);

/**
 * CONFIGURING SCORING — the bands, the thresholds, the factor weights and the N/A treatment.
 * §7b.2 — `erp_administrator` **only, and the preview counts as configuring**.
 *
 * §1.9: the Administrator *"owns scoring configuration"* and the Contributor *"cannot configure
 * scoring"*. The preview is the same endpoint and the same role check, so a screen that hides
 * Save must hide the preview button with it rather than offering a dry run nobody may commit.
 */
export const canConfigureScoring = (context: ErpContext | null): boolean =>
  holds(context, ERP_ROLE.administrator);
